import { IconClose, IconVisibility, IconVisibilityOff } from "@/assets/icons";
import { useFormSchema } from "@/components/elements/Form";
import { useFormField } from "@/components/elements/FormField/FormField";
import { IconBox } from "@/components/elements/IconBox";
import { useDebounce } from "@/hooks/useDebounce";
import { useMount } from "@/hooks/useMount";
import { useVisible } from "@/hooks/useVisible";
import { background, blue, divider, lightBlue, state, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import { convertCommasToNumber, convertNumberToCommas, convertNumberToZipCode, convertToPhoneNumber, handleKatakanaToFurigana } from "@/utils/input";
import { toNumber } from "@/utils/number";
import {
  convertFullWidthToHalfWidth,
  convertFullWidthToHalfWidthForMatchLicenseNumber,
  convertHalfWidthToFullWidth,
  convertKanaToHiragana,
  sanitizeInput,
} from "@/utils/pieces";
import { InputAdornment, TextField, TextFieldProps, css, styled } from "@mui/material";
import { AnimationEventHandler, ChangeEvent, FC, FocusEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";

export type TInputSize = "md" | "lg";

export type TInputProps = {
  size?: TInputSize;
  readonly?: boolean;
  previousValue?: string | number;
  clearable?: boolean;
  nonControl?: boolean;
  endAdornment?: ReactNode;
  startAdornment?: ReactNode;
  previous?: boolean;
  pattern?: RegExp;
  cleanWhitespace?: boolean;
  transformToFullWidth?: boolean;
  transformToHalfWidth?: boolean;
  transformToUpperCase?: boolean;
  commasFormat?: boolean;
  nonInteractive?: boolean;
  disableFieldOnChange?: boolean;
  decimalPlaces?: number;
  colorPrevious?: string;
} & Omit<TextFieldProps, "size" | "readOnly">;

export const Input: FC<TInputProps> = ({
  size = "md",
  readonly = false,
  previousValue,
  clearable = true,
  nonControl = false,
  endAdornment,
  startAdornment,
  type,
  placeholder = "入力してください",
  cleanWhitespace,
  transformToFullWidth,
  transformToHalfWidth,
  commasFormat,
  nonInteractive = false,
  disableFieldOnChange = false,
  decimalPlaces,
  colorPrevious = lightBlue[60],
  ...rest
}) => {
  const ref = useRef<HTMLInputElement>();
  const { field, fieldState, setValue } = useFormField(nonControl);
  const { visible, toggle } = useVisible();
  const isMounted = useMount();
  const fieldName = field?.name;
  const fieldsMeta = useFormSchema();
  const [isAutofilled, setIsAutofilled] = useState(false);

  const handleClear = () => {
    if (!ref.current) return;
    ref.current.value = "";
    field?.onChange("");
    field?.onBlur();

    rest.onChange?.({ target: ref.current } as ChangeEvent<HTMLInputElement>);
    rest?.onBlur?.({ target: ref.current } as FocusEvent<HTMLInputElement>);
  };

  const isCommasFormat = commasFormat || (fieldName && fieldsMeta[fieldName]?.type === "number" && commasFormat !== false);
  const value = rest.value ?? field?.value;

  const inputEndAdornment = useMemo(() => {
    if (value || isAutofilled) {
      if (!clearable || readonly || rest.disabled) return endAdornment;
      if (type === "password") {
        return (
          <InputAdornment position="end">
            <IconBox onClick={toggle}>{visible ? <IconVisibilityOff fontSize="20px" /> : <IconVisibility fontSize="20px" />}</IconBox>
          </InputAdornment>
        );
      }
      return (
        <InputAdornment position="end">
          <IconBox onClick={handleClear}>
            <IconClose fontSize="20px" />
          </IconBox>
        </InputAdornment>
      );
    }
    return endAdornment;
  }, [value, clearable, endAdornment, readonly, rest.disabled, visible, isAutofilled]);

  const isPrevious = useMemo(() => {
    if (fieldState?.isDirty === false) return true;
    return Boolean(previousValue) && (field?.value ?? rest.value) == previousValue;
  }, [fieldState?.isDirty, previousValue, value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    field?.onChange(e);
    rest.onChange?.(e);
  };

  const handleBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    const input = e.target;
    let newValue = input.value;
    if (cleanWhitespace) {
      newValue = e.target.value.replace(/\s+/g, " ").trim();
    }
    if (fieldName && fieldsMeta[fieldName]?.meta?.isEmptyToNull) {
      newValue = sanitizeInput(newValue);
    }
    if (fieldName && fieldsMeta[fieldName]?.meta?.katakanaToHiragana) {
      newValue = convertKanaToHiragana(newValue);
    }
    if (fieldName && fieldsMeta[fieldName]?.meta?.isFurigana) {
      newValue = handleKatakanaToFurigana(newValue);
    }
    if ((fieldName && fieldsMeta[fieldName]?.meta?.autoTransformToFullWidth) || transformToFullWidth) {
      newValue = convertHalfWidthToFullWidth(newValue);
    }
    if ((fieldName && fieldsMeta[fieldName]?.meta?.autoTransformToHalfWidth) || transformToHalfWidth) {
      newValue = convertFullWidthToHalfWidth(newValue);
    }
    if (fieldName && fieldsMeta[fieldName]?.meta?.autoTransformToUpperCase) {
      newValue = newValue.toUpperCase();
    }
    if (fieldName && fieldsMeta[fieldName]?.meta?.removeSpaces) {
      newValue = newValue.replace(/\s+/g, "");
    }
    if (isCommasFormat) {
      newValue = convertNumberToCommas(newValue).toString();
    }
    if (fieldName && fieldsMeta[fieldName]?.meta?.isZipCode) {
      newValue = convertNumberToZipCode(newValue);
    }
    if (fieldName && fieldsMeta[fieldName]?.type === "number" && decimalPlaces) {
      if (newValue) newValue = parseFloat(toNumber(newValue).toFixed(decimalPlaces))?.toString();
    }
    if (fieldName && fieldsMeta[fieldName]?.meta?.autoTransformToHalfWidthForMatchLicenseNumber) {
      newValue = convertFullWidthToHalfWidthForMatchLicenseNumber(newValue);
    }
    if (fieldName && fieldsMeta[fieldName]?.meta?.isPhone) {
      newValue = convertToPhoneNumber(newValue);
    }
    input.value = newValue;
    field?.onBlur();
    rest?.onBlur?.(e);
    if (!disableFieldOnChange) {
      field?.onChange?.(e);
      rest.onChange?.(e);
    }
  };

  const handleFocus: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    const input = e.target;
    let newValue = input.value;
    if (isCommasFormat) {
      newValue = convertCommasToNumber(newValue).toString();
    }
    input.value = newValue;
    rest?.onFocus?.(e);

    if (!disableFieldOnChange) {
      field?.onChange?.(e);
      rest.onChange?.(e);
    }
  };

  const makeAnimationStartHandler: AnimationEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    const target = e.target as HTMLInputElement | null;
    const autofilled = !!target?.matches("*:-webkit-autofill");
    if (e.animationName === "mui-auto-fill") {
      setIsAutofilled(autofilled);
    }

    if (e.animationName === "mui-auto-fill-cancel") {
      setIsAutofilled(autofilled);
    }
  };

  useEffect(() => {
    if (!ref.current) return;
    if (!isMounted) return;
    if (value == null || value === "") return;

    if (isCommasFormat) {
      const numberValue = convertCommasToNumber(String(value));
      const displayValue = convertNumberToCommas(String(numberValue));
      if (fieldName && setValue) {
        setValue(fieldName, displayValue, { shouldDirty: false });
      }
    }

    if (fieldName && fieldsMeta[fieldName]?.meta?.isZipCode) {
      const displayValue = convertNumberToZipCode(String(value));
      if (fieldName && setValue) {
        setValue(fieldName, displayValue, { shouldDirty: false });
      }
    }
  }, [isMounted]);

  return (
    <StyledInput
      error={Boolean(fieldState?.error)}
      inputSize={size}
      inputProps={{ ref, onAnimationStart: makeAnimationStartHandler }}
      readonly={readonly}
      disabled={readonly}
      previous={isPrevious}
      colorPrevious={colorPrevious}
      InputProps={{
        endAdornment: inputEndAdornment,
        startAdornment,
        style: nonInteractive ? { pointerEvents: "none" } : {},
      }}
      type={type === "password" && visible ? "text" : type}
      placeholder={type === "password" ? "••••••••" : placeholder}
      {...rest}
      {...field}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
    />
  );
};

export const DebounceInput: FC<TInputProps> = (props) => {
  const { field, fieldState } = useFormField(props.nonControl);
  const [value, setValue] = useState(field?.value ?? props.value ?? "");
  const { pattern, error } = props;

  useDebounce(() => {
    if (value === (field?.value ?? props.value)) return;
    field?.onChange(value);
    props.onChange?.({ target: { value } } as ChangeEvent<HTMLInputElement>);
  }, [value]);

  useEffect(() => {
    setValue(field?.value ?? props.value ?? "");
  }, [field?.value, props.value]);

  const onBlur = () => {
    field?.onChange(value);
    props.onChange?.({ target: { value } } as ChangeEvent<HTMLInputElement>);
    field?.onBlur();
  };

  const isPrevious = useMemo(() => {
    if (fieldState?.isDirty === false) return true;
    return Boolean(props.previousValue) && value === props.previousValue;
  }, [fieldState?.isDirty, props.previousValue, value]);

  return (
    <Input
      {...props}
      error={error || (pattern ? !pattern.test(value) : undefined)}
      previous={isPrevious}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={onBlur}
    />
  );
};

const options = { shouldForwardProp: (propName: string) => !["inputSize", "previous", "readonly", "colorPrevious"].includes(propName) };

const StyledInput = styled(TextField, options)<{ inputSize: TInputSize; readonly: boolean; previous: boolean; colorPrevious: string }>`
  .MuiInputBase-root {
    background: ${background.white};
    padding-right: 8px;
    white-space: nowrap;
    input {
      color: ${text.primary};
      caret-color: ${blue[70]};
      &::placeholder {
        opacity: 1;
        color: ${text.tertiary}!important;
      }
    }
    fieldset {
      border-color: ${divider.middle};
      border-radius: ${rounded.xs};
      border-width: 1px !important;
    }
    &:hover,
    &.Mui-focused {
      fieldset {
        border-color: ${blue[70]};
      }
    }
    &.Mui-error {
      background: ${background.error};
      fieldset {
        border-color: ${state.error_1}!important;
      }
    }
    &.Mui-disabled {
      background: ${background.disable};
      fieldset {
        border-color: ${divider.middle}!important;
      }
    }
    ${({ inputSize }) => {
      if (inputSize === "md") return MediumInput;
      if (inputSize === "lg") return LargeInput;
      return MediumInput;
    }};
    ${({ readonly }) => readonly && readonlyInput};
    ${({ previous, colorPrevious }) =>
      previous &&
      css`
        input {
          color: ${colorPrevious};
        }
      `};
  }
`;

const readonlyInput = css`
  background: ${background.disable} !important;
  pointer-events: none;
  -webkit-text-fill-color: ${text.primary};
  input:-webkit-autofill {
    -webkit-background-clip: text;
    box-shadow: inset 0 0 20px 20px ${background.disable};
  }
  input {
    color: ${text.primary}!important;
    -webkit-text-fill-color: ${text.primary};
    &::placeholder {
      opacity: 0;
    }
  }
  fieldset {
    border-color: ${divider.middle}!important;
  }
`;

const MediumInput = css`
  .MuiInputBase-input {
    padding: 6px 0px;
    padding-left: 8px;
    ${css(typography.body14)};
  }
`;

const LargeInput = css`
  .MuiInputBase-input {
    padding: 12px 8px;
    ${css(typography.body14)};
  }
`;
